import {
	DEFAULT_ASSIGNMENT_CONFIRMATION_MONTHLY_TASKS,
	DEFAULT_ASSIGNMENT_CONFIRMATION_PRICING_NOTES,
	DEFAULT_ASSIGNMENT_CONFIRMATION_REGULAR_TASKS,
	DEFAULT_ASSIGNMENT_CONFIRMATION_SERVICE_AREAS,
	DEFAULT_ASSIGNMENT_CONFIRMATION_SUBTITLE,
} from '@/utils/assignmentConfirmationDocument';
import { listToBulletedText, splitBulletedText } from '@/utils/bulletedTextarea';

export const MAX_ASSIGNMENT_SUBTITLE_OPTIONS = 50;
export const MAX_ASSIGNMENT_SUBTITLE_LENGTH = 160;
export const MAX_ASSIGNMENT_SERVICE_AREA_OPTIONS = MAX_ASSIGNMENT_SUBTITLE_OPTIONS;
export const MAX_ASSIGNMENT_SERVICE_AREA_LENGTH = MAX_ASSIGNMENT_SUBTITLE_LENGTH;
export const MAX_ASSIGNMENT_REGULAR_TASKS = 100;
export const MAX_ASSIGNMENT_REGULAR_TASK_LENGTH = 500;
export const MAX_ASSIGNMENT_MONTHLY_TASKS = MAX_ASSIGNMENT_REGULAR_TASKS;
export const MAX_ASSIGNMENT_MONTHLY_TASK_LENGTH = MAX_ASSIGNMENT_REGULAR_TASK_LENGTH;
export const MAX_ASSIGNMENT_PRICING_NOTES = MAX_ASSIGNMENT_REGULAR_TASKS;
export const MAX_ASSIGNMENT_PRICING_NOTE_LENGTH = MAX_ASSIGNMENT_REGULAR_TASK_LENGTH;

const normalizeAssignmentTextOptions = (values, maxOptions, maxLength) => {
	if (!Array.isArray(values)) return [];

	const normalized = [];
	const seen = new Set();
	for (const value of values) {
		if (normalized.length >= maxOptions) break;
		const option = String(value ?? '').trim().slice(0, maxLength);
		const key = option.toLocaleLowerCase();
		if (!option || seen.has(key)) continue;
		seen.add(key);
		normalized.push(option);
	}
	return normalized;
};

export const normalizeAssignmentSubtitleOptions = (values) =>
	normalizeAssignmentTextOptions(
		values,
		MAX_ASSIGNMENT_SUBTITLE_OPTIONS,
		MAX_ASSIGNMENT_SUBTITLE_LENGTH,
	);

export const normalizeAssignmentServiceAreaOptions = (values) =>
	normalizeAssignmentTextOptions(
		values,
		MAX_ASSIGNMENT_SERVICE_AREA_OPTIONS,
		MAX_ASSIGNMENT_SERVICE_AREA_LENGTH,
	);

export const normalizeAssignmentRegularTasksTemplate = (values) =>
	normalizeAssignmentTextOptions(
		values,
		MAX_ASSIGNMENT_REGULAR_TASKS,
		MAX_ASSIGNMENT_REGULAR_TASK_LENGTH,
	);

export const normalizeAssignmentMonthlyTasksTemplate = (values) =>
	normalizeAssignmentTextOptions(
		values,
		MAX_ASSIGNMENT_MONTHLY_TASKS,
		MAX_ASSIGNMENT_MONTHLY_TASK_LENGTH,
	);

export const normalizeAssignmentPricingNotesTemplate = (values) =>
	normalizeAssignmentTextOptions(
		values,
		MAX_ASSIGNMENT_PRICING_NOTES,
		MAX_ASSIGNMENT_PRICING_NOTE_LENGTH,
	);

const assignmentTaskKey = (value) => String(value ?? '').trim().toLocaleLowerCase();

const appendNewAssignmentTaskCatalogItems = (
	previousCatalog,
	savedCatalog,
	currentTasks,
	normalizeCatalog,
) => {
	const nextTasks = Array.isArray(currentTasks) ? [...currentTasks] : [];
	const previousKeys = new Set(normalizeCatalog(previousCatalog).map(assignmentTaskKey));
	const currentKeys = new Set(nextTasks.map(assignmentTaskKey).filter(Boolean));

	for (const task of normalizeCatalog(savedCatalog)) {
		const key = assignmentTaskKey(task);
		if (previousKeys.has(key) || currentKeys.has(key)) continue;
		currentKeys.add(key);
		nextTasks.push(task);
	}

	return nextTasks;
};

export const appendNewAssignmentRegularTasksFromCatalog = (
	previousCatalog,
	savedCatalog,
	currentTasks,
) =>
	appendNewAssignmentTaskCatalogItems(
		previousCatalog,
		savedCatalog,
		currentTasks,
		normalizeAssignmentRegularTasksTemplate,
	);

export const appendNewAssignmentMonthlyTasksFromCatalog = (
	previousCatalog,
	savedCatalog,
	currentTasks,
) =>
	appendNewAssignmentTaskCatalogItems(
		previousCatalog,
		savedCatalog,
		currentTasks,
		normalizeAssignmentMonthlyTasksTemplate,
	);

export const appendNewAssignmentPricingNotesFromCatalog = (
	previousCatalog,
	savedCatalog,
	currentNotes,
) =>
	appendNewAssignmentTaskCatalogItems(
		previousCatalog,
		savedCatalog,
		currentNotes,
		normalizeAssignmentPricingNotesTemplate,
	);

export const parseAssignmentRegularTasksTemplateText = (value) =>
	normalizeAssignmentRegularTasksTemplate(splitBulletedText(value));

export const formatAssignmentRegularTasksTemplateText = (values) =>
	listToBulletedText(normalizeAssignmentRegularTasksTemplate(values));

export const parseAssignmentMonthlyTasksTemplateText = (value) =>
	normalizeAssignmentMonthlyTasksTemplate(splitBulletedText(value));

export const formatAssignmentMonthlyTasksTemplateText = (values) =>
	listToBulletedText(normalizeAssignmentMonthlyTasksTemplate(values));

export const parseAssignmentPricingNotesTemplateText = (value) =>
	normalizeAssignmentPricingNotesTemplate(splitBulletedText(value));

export const formatAssignmentPricingNotesTemplateText = (values) =>
	listToBulletedText(normalizeAssignmentPricingNotesTemplate(values));

export const getAssignmentSubtitleOptions = (documentPresets) => {
	const assignmentSettings = documentPresets?.assignmentConfirmation;
	if (Array.isArray(assignmentSettings?.subtitleOptions)) {
		return normalizeAssignmentSubtitleOptions(assignmentSettings.subtitleOptions);
	}
	return [DEFAULT_ASSIGNMENT_CONFIRMATION_SUBTITLE];
};

export const getAssignmentServiceAreaOptions = (documentPresets) => {
	const assignmentSettings = documentPresets?.assignmentConfirmation;
	if (Array.isArray(assignmentSettings?.serviceAreaOptions)) {
		return normalizeAssignmentServiceAreaOptions(assignmentSettings.serviceAreaOptions);
	}
	return [...DEFAULT_ASSIGNMENT_CONFIRMATION_SERVICE_AREAS];
};

export const getAssignmentRegularTasksTemplate = (documentPresets) => {
	const assignmentSettings = documentPresets?.assignmentConfirmation;
	if (Array.isArray(assignmentSettings?.regularTasksTemplate)) {
		return normalizeAssignmentRegularTasksTemplate(assignmentSettings.regularTasksTemplate);
	}
	return [...DEFAULT_ASSIGNMENT_CONFIRMATION_REGULAR_TASKS];
};

export const getAssignmentMonthlyTasksTemplate = (documentPresets) => {
	const assignmentSettings = documentPresets?.assignmentConfirmation;
	if (Array.isArray(assignmentSettings?.monthlyTasksTemplate)) {
		return normalizeAssignmentMonthlyTasksTemplate(assignmentSettings.monthlyTasksTemplate);
	}
	return [...DEFAULT_ASSIGNMENT_CONFIRMATION_MONTHLY_TASKS];
};

export const getAssignmentPricingNotesTemplate = (documentPresets) => {
	const assignmentSettings = documentPresets?.assignmentConfirmation;
	if (Array.isArray(assignmentSettings?.pricingNotesTemplate)) {
		return normalizeAssignmentPricingNotesTemplate(assignmentSettings.pricingNotesTemplate);
	}
	return [...DEFAULT_ASSIGNMENT_CONFIRMATION_PRICING_NOTES];
};

const mergeAssignmentOptions = (documentPresets, key, values) => {
	const rootSettings =
		documentPresets && typeof documentPresets === 'object' && !Array.isArray(documentPresets)
			? documentPresets
			: {};
	const assignmentSettings =
		rootSettings.assignmentConfirmation &&
		typeof rootSettings.assignmentConfirmation === 'object' &&
		!Array.isArray(rootSettings.assignmentConfirmation)
			? rootSettings.assignmentConfirmation
			: {};

	return {
		...rootSettings,
		version: 1,
		assignmentConfirmation: {
			...assignmentSettings,
			[key]: values,
		},
	};
};

export const mergeAssignmentSubtitleOptions = (documentPresets, values) =>
	mergeAssignmentOptions(
		documentPresets,
		'subtitleOptions',
		normalizeAssignmentSubtitleOptions(values),
	);

export const mergeAssignmentServiceAreaOptions = (documentPresets, values) =>
	mergeAssignmentOptions(
		documentPresets,
		'serviceAreaOptions',
		normalizeAssignmentServiceAreaOptions(values),
	);

export const mergeAssignmentRegularTasksTemplate = (documentPresets, values) =>
	mergeAssignmentOptions(
		documentPresets,
		'regularTasksTemplate',
		normalizeAssignmentRegularTasksTemplate(values),
	);

export const mergeAssignmentMonthlyTasksTemplate = (documentPresets, values) =>
	mergeAssignmentOptions(
		documentPresets,
		'monthlyTasksTemplate',
		normalizeAssignmentMonthlyTasksTemplate(values),
	);

export const mergeAssignmentPricingNotesTemplate = (documentPresets, values) =>
	mergeAssignmentOptions(
		documentPresets,
		'pricingNotesTemplate',
		normalizeAssignmentPricingNotesTemplate(values),
	);

export const applyAssignmentTaskTemplatesToNewContent = (content, documentPresets) => {
	const source = content && typeof content === 'object' && !Array.isArray(content) ? content : {};
	const scope =
		source.scope && typeof source.scope === 'object' && !Array.isArray(source.scope)
			? source.scope
			: {};
	const pricing =
		source.pricing && typeof source.pricing === 'object' && !Array.isArray(source.pricing)
			? source.pricing
			: {};

	return {
		...source,
		scope: {
			...scope,
			serviceAreas: getAssignmentServiceAreaOptions(documentPresets),
			regularTasks: getAssignmentRegularTasksTemplate(documentPresets),
			monthlyTasks: getAssignmentMonthlyTasksTemplate(documentPresets),
		},
		pricing: {
			...pricing,
			notes: getAssignmentPricingNotesTemplate(documentPresets),
		},
	};
};

// Kept for callers created before the reusable list catalogs were introduced.
export const applyAssignmentRegularTasksTemplateToNewContent =
	applyAssignmentTaskTemplatesToNewContent;
